import {
  ErrorModal,
  MesfModal,
  dxtToLocalServerTime,
  useUTLSettingsContext,
} from "@dexteel/mesf-core";
import {
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form";
import { CenteredLazyLoading } from "../../../../../controls/LazyLoading";
import { NotificationSnackBar } from "../../../../../controls/snackbars/notification-snackbar";
import { DelayCategoryAutocomplete } from "../../../../../shared/autocompletes/DelayCategoryAutocomplete";
import { CommentsField } from "../../../../../shared/fields/CommentsField";
import { DelayCodeTreePicker } from "../../../../../shared/treepickers/DelayCodeTreePicker";
import { useSplitDelayMutation } from "../../../hooks/mutations";
import { useDelayByIdQuery } from "../../../hooks/queries";
import CustomTimeSlider from "../../../shared/CustomTimeSlider";
import type { DelayByShift } from "../../models/DelayByShift";
import { splitDelaysStyles } from "../../styles/splitDelaysStyles";

const formatDuration = (duration: number): string => {
  const hours = Math.floor(duration / (1000 * 60 * 60));
  const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((duration % (1000 * 60)) / 1000);
  return `${hours} hs ${minutes} min ${seconds} sec`;
};

type Props = {
  show: boolean;
  onHide: () => void;
  delayId: number | null;
  assetId: number | null;
};

interface CombinedDelayForm {
  delay1: DelayByShift;
  delay2: DelayByShift;
}

export const SplitDelays = ({ show, onHide, delayId, assetId }: Props) => {
  const theme = useTheme();
  const styles = splitDelaysStyles(theme);

  const { state } = useUTLSettingsContext();
  const minDelayDuration = Number(state?.settings?.MinValidDelay ?? 10);

  const [startDateSlider, setStartDateSlider] = useState<Date>(new Date());
  const [endDateSlider, setEndDateSlider] = useState<Date>(new Date());
  const [error, setError] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationErrorMessage, setNotificationErrorMessage] = useState("");

  const splitMutation = useSplitDelayMutation();

  const { data: delayData, isLoading } = useDelayByIdQuery(
    show ? delayId : null,
  );

  const { control, handleSubmit, setValue, getValues, reset } =
    useForm<CombinedDelayForm>({
      defaultValues: {
        delay1: {} as DelayByShift,
        delay2: {} as DelayByShift,
      },
    });

  const [delay1End, delay1Start, delay2End, delay2Start] = useWatch({
    control,
    name: ["delay1.End", "delay1.Start", "delay2.End", "delay2.Start"],
  });

  // Populate form when delay data arrives
  useEffect(() => {
    reset();
    if (show && delayData) {
      const startDate = delayData.Start
        ? new Date(delayData.Start)
        : new Date();
      const endDate = delayData.End ? new Date(delayData.End) : new Date();
      const middleDate = new Date(
        (startDate.getTime() + endDate.getTime()) / 2,
      );

      setStartDateSlider(startDate);
      setEndDateSlider(endDate);

      reset({
        delay1: {
          DelayId: delayData.DelayId,
          Start: startDate,
          End: middleDate,
          DelayCategoryId: delayData.DelayCategoryId,
          DelayCodeId: delayData.DelayCodeId,
          Comments: delayData.Comments,
        },
        delay2: {
          Start: middleDate,
          End: delayData.End ? endDate : null,
        },
      });
    }
  }, [show, delayData]);

  const onSubmit: SubmitHandler<CombinedDelayForm> = (
    data: CombinedDelayForm,
  ) => {
    const d1Start = data.delay1.Start?.getTime();
    const d1End = data.delay1.End?.getTime();
    const d2Start = data.delay2.Start?.getTime();
    const d2End = data.delay2.End?.getTime();

    if (d1Start == null || d1End == null || d2Start == null || d2End == null) {
      setNotificationErrorMessage(
        "Start and End dates are required for both delays",
      );
      return;
    }

    if (
      d1End - d1Start <= minDelayDuration * 1000 ||
      d2End - d2Start <= minDelayDuration * 1000
    ) {
      setNotificationErrorMessage(
        `The minimum duration allowed is ${minDelayDuration} seconds`,
      );
      return;
    }

    splitMutation.mutate(
      { delay1: data.delay1, delay2: data.delay2 },
      {
        onSuccess: () => {
          setNotificationMessage("The operation was completed successfully");
          onHide();
        },
        onError: (err) => {
          setError(
            err instanceof Error ? err.message : "Failed to split delay",
          );
        },
      },
    );
  };

  if (isLoading && show && delayId) {
    return <CenteredLazyLoading />;
  }

  return (
    <>
      <MesfModal
        title="SPLIT DELAY"
        open={show}
        handleClose={() => onHide()}
        id="split-delays-modal"
        maxWidth="lg"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <MesfModal.Content dividers>
            <Grid container justifyContent="center">
              <Grid
                item
                md={10}
                style={{ marginBottom: 10 }}
                sx={styles.sliderContainer}
              >
                <Typography
                  sx={{
                    ...styles.durationLabel,
                    left: "25%",
                    color: theme.palette.secondary.main,
                  }}
                >
                  {delay1End && delay1Start
                    ? formatDuration(
                        delay1End.getTime() - delay1Start.getTime(),
                      )
                    : ""}
                </Typography>
                <Typography
                  sx={{
                    ...styles.durationLabel,
                    left: "75%",
                    color: theme.palette.success.main,
                  }}
                >
                  {delay2End && delay2Start
                    ? formatDuration(
                        delay2End.getTime() - delay2Start.getTime(),
                      )
                    : ""}
                  ,
                </Typography>
                <CustomTimeSlider
                  startTime={startDateSlider}
                  endTime={endDateSlider}
                  onTimeChange={(newTime: Date) => {
                    setValue("delay1.End", newTime);
                    setValue("delay2.Start", newTime);
                  }}
                />
              </Grid>
              <Grid item container md={5}>
                <Typography>Delay 1</Typography>
                <Grid
                  item
                  container
                  md={12}
                  spacing={1}
                  style={{ marginTop: 10 }}
                >
                  <Grid item md={6}>
                    <Controller
                      name="delay1.Start"
                      control={control}
                      rules={{ required: "Delay code is required" }}
                      render={({
                        field,
                        fieldState: { error: fieldError },
                      }) => (
                        <TextField
                          label="Start"
                          variant="outlined"
                          value={dxtToLocalServerTime(field?.value, "HH:mm:ss")}
                          error={!!fieldError}
                          helperText={fieldError?.message}
                          disabled
                          fullWidth
                          margin="dense"
                          autoComplete="off"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item md={6}>
                    <Controller
                      name="delay1.DelayCodeId"
                      control={control}
                      render={({
                        field,
                        fieldState: { error: fieldError },
                      }) => (
                        <DelayCodeTreePicker
                          label="Code"
                          value={field.value}
                          onChange={field.onChange}
                          assetId={assetId ?? null}
                          fieldError={fieldError}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item md={6}>
                    <Controller
                      name="delay1.End"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          label="End"
                          variant="outlined"
                          value={dxtToLocalServerTime(field?.value, "HH:mm:ss")}
                          disabled
                          fullWidth
                          margin="dense"
                          autoComplete="off"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item md={6}>
                    <Controller
                      name="delay1.DelayCategoryId"
                      control={control}
                      render={({
                        field,
                        fieldState: { error: fieldError },
                      }) => (
                        <DelayCategoryAutocomplete
                          value={field.value}
                          onChange={field.onChange}
                          assetId={assetId ?? null}
                          fieldError={fieldError}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item md={12}>
                    <Controller
                      name="delay1.Comments"
                      control={control}
                      render={({
                        field,
                        fieldState: { error: fieldError },
                      }) => (
                        <CommentsField
                          value={field.value}
                          onChange={field.onChange}
                          fieldError={fieldError}
                          getValues={getValues}
                          rows={{ minRows: 3, maxRows: 3 }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item md={1} />
              <Grid item container md={5}>
                <Typography>Delay 2</Typography>
                <Grid
                  item
                  container
                  md={12}
                  spacing={1}
                  style={{ marginTop: 10 }}
                >
                  <Grid item md={6}>
                    <Controller
                      name="delay2.Start"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          label="Start"
                          variant="outlined"
                          value={dxtToLocalServerTime(field?.value, "HH:mm:ss")}
                          disabled
                          fullWidth
                          margin="dense"
                          autoComplete="off"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item md={6}>
                    <Controller
                      name="delay2.DelayCodeId"
                      control={control}
                      render={({
                        field,
                        fieldState: { error: fieldError },
                      }) => (
                        <DelayCodeTreePicker
                          label="Code"
                          value={field.value}
                          onChange={field.onChange}
                          assetId={assetId ?? null}
                          fieldError={fieldError}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item md={6}>
                    <Controller
                      name="delay2.End"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          label="End"
                          variant="outlined"
                          value={
                            field.value
                              ? dxtToLocalServerTime(field.value, "HH:mm:ss")
                              : ""
                          }
                          disabled
                          fullWidth
                          margin="dense"
                          autoComplete="off"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item md={6}>
                    <Controller
                      name="delay2.DelayCategoryId"
                      control={control}
                      render={({
                        field,
                        fieldState: { error: fieldError },
                      }) => (
                        <DelayCategoryAutocomplete
                          value={field.value}
                          onChange={field.onChange}
                          assetId={assetId ?? null}
                          fieldError={fieldError}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item md={12}>
                    <Controller
                      name="delay2.Comments"
                      control={control}
                      render={({
                        field,
                        fieldState: { error: fieldError },
                      }) => (
                        <CommentsField
                          value={field.value}
                          onChange={field.onChange}
                          fieldError={fieldError}
                          getValues={getValues}
                          rows={{ minRows: 3, maxRows: 3 }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </MesfModal.Content>
          <MesfModal.Actions style={{ padding: "20px 90px 30px" }}>
            <Grid container spacing={1} justifyContent="flex-end">
              <Grid item md={2} xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  color="inherit"
                  onClick={() => onHide()}
                  size="medium"
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item md={2} xs={12}>
                <Button
                  fullWidth
                  startIcon={
                    splitMutation.isPending && <CircularProgress size="1rem" />
                  }
                  disabled={splitMutation.isPending}
                  variant="contained"
                  color="primary"
                  type="submit"
                  size="medium"
                >
                  Split
                </Button>
              </Grid>
            </Grid>
          </MesfModal.Actions>
        </form>
      </MesfModal>
      <NotificationSnackBar
        message={notificationErrorMessage}
        onHide={() => setNotificationErrorMessage("")}
        severity="error"
      />
      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
      <ErrorModal
        error={error}
        onHide={() => setError("")}
        title="Error Splitting Delays"
      />
    </>
  );
};
